'use client';

import { Building2, Calculator, Menu, X, Share2 } from 'lucide-react';
import { useState, useCallback, useEffect } from 'react';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { CurrencyPicker } from '@/components/CurrencyPicker';
import { PrintButton } from '@/components/ui/PrintButton';
import { ShiftKeyTooltip } from '@/components/ui/ShiftKeyTooltip';
import { ShareButton } from '@/components/calculator/ShareButton';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useKeyboardNavigation } from '@/hooks/useKeyboardNavigation';
import { useShiftKeyTooltips } from '@/hooks/useShiftKeyTooltips';
import { useShareMode } from '@/components/ShareModeProvider';
import Link from 'next/link';
import type { SupportedCurrency } from '@/lib/env';
import { FEATURE_FLAGS } from '@/lib/env';

interface MainNavbarProps {
  currency?: SupportedCurrency;
  onCurrencyChange?: (currency: SupportedCurrency) => void;
  showPrintButton?: boolean;
  currentPage?: 'home' | 'about' | 'examples' | 'changelog' | 'v2';
  isShareMode?: boolean;
  showShareButton?: boolean;
}

export function MainNavbar({ 
  currency = 'NZD', 
  onCurrencyChange, 
  showPrintButton = false,
  currentPage = 'home',
  isShareMode = false,
  showShareButton = false
}: MainNavbarProps) {
  const { shareData } = useShareMode();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const showShiftTooltips = useShiftKeyTooltips();
  
  // Focus trap for mobile menu
  const mobileMenuRef = useFocusTrap({ 
    isActive: mobileMenuOpen,
    autoFocus: true,
    restoreFocus: true 
  });

  // Keyboard navigation for desktop menu
  const desktopNavRef = useKeyboardNavigation({
    orientation: 'horizontal',
    wrap: true,
  });

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const handleShareClick = useCallback(() => {
    // Always use the event system that ShareButton listens for
    const shareEvent = new CustomEvent('triggerShare');
    document.dispatchEvent(shareEvent);
  }, []);

  // Handle escape key for mobile menu
  useEffect(() => {
    if (!mobileMenuOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMobileMenu();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen, closeMobileMenu]);
  
  return (
    <>
      <header className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-md supports-[backdrop-filter]:bg-card/60">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            href="/" 
            className="flex items-center gap-3 z-50"
            aria-label="AccessNow ROI Calculator - Go to homepage"
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-accessnow-orange text-white">
              <Building2 className="h-5 w-5" aria-hidden="true" />
            </div>
            <div className="text-xl font-bold">
              <span className="text-accessnow-orange">Access</span>
              <span className="text-foreground">Now</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          {!isShareMode && (
            <nav id="main-navigation" className="hidden sm:flex flex-1 justify-center" aria-label="Main navigation">
              <div ref={desktopNavRef} className="flex items-center gap-4" role="list">
                {!FEATURE_FLAGS.hideAboutPage && (
                  <ShiftKeyTooltip 
                    shortcut="A" 
                    show={showShiftTooltips}
                    position="bottom"
                  >
                    <Link href="/about" role="listitem">
                      <button 
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === 'about' 
                            ? 'bg-accessnow-orange text-white' 
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }`}
                        aria-current={currentPage === 'about' ? 'page' : undefined}
                        aria-label="About AccessNow"
                      >
                        About
                      </button>
                    </Link>
                  </ShiftKeyTooltip>
                )}
                
                <ShiftKeyTooltip 
                  shortcut="L" 
                  show={showShiftTooltips}
                  position="bottom"
                >
                  <Link href="/calculator" role="listitem">
                    <button 
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        ['home', 'v2'].includes(currentPage) 
                          ? 'bg-accessnow-orange text-white' 
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                      aria-current={['home', 'v2'].includes(currentPage) ? 'page' : undefined}
                      aria-label="ROI Calculator"
                    >
                      <Calculator className="h-4 w-4" aria-hidden="true" />
                      Calculator
                    </button>
                  </Link>
                </ShiftKeyTooltip>
                
                <ShiftKeyTooltip 
                  shortcut="E" 
                  show={showShiftTooltips}
                  position="bottom"
                >
                  <Link href="/examples">
                    <button className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === 'examples' 
                        ? 'bg-accessnow-orange text-white' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}>
                      Examples
                    </button>
                  </Link>
                </ShiftKeyTooltip>
              </div>
            </nav>
          )}

          {/* Desktop Actions */}
          <div className="hidden sm:flex items-center gap-2">
            {!isShareMode && !['changelog', 'examples', 'about', 'v2', 'home'].includes(currentPage) && (
              <Link href="/changelog">
                <button className="text-sm flex items-center gap-1 px-2 py-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
                  Changelog
                </button>
              </Link>
            )}
            
            {!isShareMode && onCurrencyChange && (
              <div>
                <CurrencyPicker
                  value={currency}
                  onChange={onCurrencyChange}
                />
              </div>
            )}
            {showShareButton && (
              <ShiftKeyTooltip 
                shortcut="Shift+S" 
                show={showShiftTooltips}
                position="bottom"
              >
                <button
                  onClick={handleShareClick}
                  className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                  aria-label="Share calculation"
                >
                  <Share2 className="h-4 w-4" />
                </button>
              </ShiftKeyTooltip>
            )}
            {showPrintButton && (
              <ShiftKeyTooltip 
                shortcut="P" 
                show={showShiftTooltips}
                position="bottom"
              >
                <PrintButton className="print-hidden" />
              </ShiftKeyTooltip>
            )}
            <ShiftKeyTooltip 
              shortcut="M" 
              show={showShiftTooltips}
              position="bottom"
            >
              <div>
                <ThemeToggle />
              </div>
            </ShiftKeyTooltip>
          </div>

          {/* Mobile Actions */}
          <div className="flex sm:hidden items-center gap-2">
            {/* Share Button - only show in share mode */}
            {showShareButton && (
              <button
                onClick={handleShareClick}
                className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                aria-label="Share calculation"
              >
                <Share2 className="h-4 w-4" />
              </button>
            )}
            {/* Print Button - mobile */}
            {showPrintButton && (
              <div className="sm:hidden">
                <PrintButton />
              </div>
            )}
            {/* Mobile Menu Button - only show if not in share mode */}
            {!isShareMode && (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
                aria-label="Toggle mobile menu"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <Menu className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {!isShareMode && mobileMenuOpen && (
          <div 
            ref={mobileMenuRef}
            id="mobile-menu"
            className="sm:hidden absolute top-16 left-0 right-0 bg-card border-b border-border shadow-lg"
            role="menu"
            aria-label="Mobile navigation menu"
          >
            <div className="p-4 space-y-4">
              {/* Mobile Navigation Links */}
              <div className="space-y-2">
                {!FEATURE_FLAGS.hideAboutPage && (
                  <Link href="/about" onClick={() => setMobileMenuOpen(false)}>
                    <button className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === 'about' 
                        ? 'bg-accessnow-orange text-white' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}>
                      About
                    </button>
                  </Link>
                )}
                
                <Link href="/calculator" onClick={() => setMobileMenuOpen(false)}>
                  <button className={`w-full text-left flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    ['home', 'v2'].includes(currentPage) 
                      ? 'bg-accessnow-orange text-white' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}>
                    <Calculator className="h-4 w-4" />
                    Calculator
                  </button>
                </Link>
                
                <Link href="/examples" onClick={() => setMobileMenuOpen(false)}>
                  <button className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === 'examples' 
                      ? 'bg-accessnow-orange text-white' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}>
                    Examples
                  </button>
                </Link>

                {!['changelog', 'examples', 'about', 'v2', 'home'].includes(currentPage) && (
                  <Link href="/changelog" onClick={() => setMobileMenuOpen(false)}>
                    <button className="w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-muted">
                      Changelog
                    </button>
                  </Link>
                )}
              </div>

              {/* Mobile Controls */}
              <div className="space-y-3 pt-2 border-t border-border">
                {/* Currency Picker */}
                {onCurrencyChange && (
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Currency</div>
                    <CurrencyPicker
                      value={currency}
                      onChange={onCurrencyChange}
                      className="w-full"
                    />
                  </div>
                )}

                {/* Theme Toggle */}
                <div className="space-y-2">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Theme</div>
                  <div className="flex justify-center">
                    <ThemeToggle />
                  </div>
                </div>

                {/* Print Button */}
                {showPrintButton && (
                  <PrintButton className="w-full print-hidden" />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      </header>
      
      {/* ShareButton component for navbar share functionality - modal only for share mode */}
      {isShareMode && shareData && (
        <ShareButton
          inputs={shareData.inputs}
          currency={currency}
          calculatorMode={shareData.calculatorMode}
          pricing={shareData.pricing}
          existingCustomName={shareData.customName}
          hasResults={true}
          renderButton={false}
        />
      )}
    </>
  );
}
